<template>
	<div :class="['search-input', { condensed: theme === 'condensed' }]">
		<slot name="label" :id="id" :label="label" :labelHidden="labelHidden">
			<label
				v-if="label"
				:for="id"
				class="search-input-label"
				:class="{ 'visually-hidden': labelHidden }"
			>
				{{ label }}
			</label>
		</slot>

		<div class="search-input-wrapper">
			<div class="position-relative">
				<input
					:id="id"
					:ref="id"
					type="search"
					v-bind="$attrs"
					v-on="{
						...$listeners,
						// the input listener here is needed to ensure
						// v-model continues to work
						input: (e) => $emit('input', e.target.value)
					}"
					:value="dataValue"
					class="search-input-text input-text"
					:class="[
						customInputClasses,
						{
							'search-input-text__disabled': isDisabled,
							'search-input-text__invalid': error
						}
					]"
					:disabled="isDisabled"
					:style="style"
					@input="onInput"
				/>
				<Icon
					class="search-icon"
					:class="{ 'search-icon__disabled': isDisabled }"
					type="search"
				/>
				<CTA
					v-if="showClearIcon"
					tag="button"
					class="clear-button"
					aria-label="Clear search input field."
					@click="onClear"
				>
					<Icon
						class="clear-icon"
						type="clear"
						ariaLabel="Clear search input field."
					/>
				</CTA>
			</div>
			<div
				class="validation-container"
				:style="`min-height: ${validationFixedHeight}`"
			>
				<ValidationErrors
					class="search-input-validation-errors"
					v-show="error"
					:errors="errorMessages"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import { ValidationErrors } from "./base";
import { input, label, style } from "./mixins";
import { CTA } from "@vg-faset/ui/src/components/cta-v2";
import { Icon } from "@vg-faset/ui/src/components/icon";

/**
 * A standard search input component.
 */
export default {
	name: "SearchInput",

	components: {
		CTA,
		Icon,
		ValidationErrors
	},

	mixins: [input, label, style],

	data() {
		return {
			dataValue: null
		};
	},

	computed: {
		showClearIcon() {
			return this.dataValue && this.dataValue.length > 0;
		}
	},

	methods: {
		onClear() {
			this.dataValue = "";
			this.$emit("input", this.dataValue);
		},

		onInput(e) {
			this.dataValue = e.target.value;
		}
	},

	mounted() {
		this.dataValue = this.value;
	},

	watch: {
		value: function (newVal) {
			this.dataValue = newVal;
		}
	}
};
</script>

<style lang="scss" scoped>
.search-input {
	display: block;
	width: 100%;
	margin: 0px;

	&-wrapper {
		position: relative;

		.position-relative {
			position: relative;
		}
	}

	&-text {
		-webkit-appearance: none;
		width: 100%;
		padding-right: 40px;
		padding-left: 40px;
		border-radius: 0;
		background-color: transparent;

		&__disabled {
			border-color: $color--neutral-8;
			color: $color--neutral-8;
			background-image: none;
			background-color: transparent;
			box-shadow: none;

			&::placeholder {
				color: $color--neutral-8;
			}
		}

		&__invalid {
			border-color: $color--red-5 !important;
		}

		&::-ms-reveal,
		&::-ms-clear {
			display: none;
			width: 0;
			height: 0;
		}

		&::-webkit-search-decoration,
		&::-webkit-search-cancel-button,
		&::-webkit-search-results-button,
		&::-webkit-search-results-decoration {
			display: none;
		}
	}

	&.condensed {
		.search-input-text {
			max-height: 48px;
			height: 100%;
			padding-top: 14px;
			padding-bottom: 12px;
			border: 2px solid $color--neutral-8;

			&:focus {
				border-color: $color--neutral-8 !important;
			}
		}
	}

	&-label {
		display: inline-block;
		font-weight: bold;
		line-height: 1;
		margin: 0 0 8px 0;
	}

	&-validation_errors {
		margin: 8px 0 0 0;
	}
}

.search-icon {
	position: absolute;
	top: 50%;
	left: 16px;
	transform: translateY(-50%);

	&__disabled {
		color: $color--neutral-8;
	}
}

.clear-button {
	position: absolute;
	top: 50%;
	right: 16px;
	transform: translateY(-50%);
	color: $color--neutral-7;

	&:hover,
	&:focus {
		color: $color--neutral-7;
	}

	.clear-icon {
		color: $color--neutral-7;
		font-size: 22px;
	}
}

.vg-is-tabbing .search-input-text:focus {
	outline-offset: 4px;
}
</style>
